<script lang="ts">
  import { page } from '$app/stores';

  import Button from '$components/ui/button.svelte';
  import Input from '$components/ui/input.svelte';
  import { t } from '$lib/i18n';
  import { currency, currentLocation } from '$lib/region';
  import { baseFormatCurrency, formatCurrency } from '$lib/utils/currency';
  import {
    SHARED_GAME_SESSION_ID_QUERY_PARAM_NAME,
    finishGame,
    getHighestScoreGameSession,
    redeemDiscount
  } from '../api/storage.client';
  import { browser } from '$app/environment';
  import Icon from '$components/ui/icon/icon.svelte';
  import { fade, fly, scale, slide } from 'svelte/transition';
  import { createEventDispatcher, onMount } from 'svelte';
  import Divider from '$components/ui/divider.svelte';
  import { quintInOut, quintOut } from 'svelte/easing';
  import Translation from '$lib/i18n/translation.svelte';
  import Checkbox from '$components/ui/checkbox.svelte';
  import { user } from '$lib/stores/user';
  import { format } from 'date-fns';
  import { createTemporaryValue } from '$lib/stores/temporary-value';
  import type { BaseGameSession, GameSession } from '../api/types';
  import Spinner from '$components/ui/spinner.svelte';
  import Skeleton from '$components/ui/skeleton.svelte';
  import { buildPath } from '$lib/utils/paths';
  import { lang } from '$lib/lang';

  const dispatch = createEventDispatcher<{ restart: undefined }>();

  export let score: number;
  export let sharePagePath: string;

  export let gameSession: BaseGameSession | GameSession | null = null;
  let bestGame: GameSession | null = null;

  let loadingBestGame = true;
  onMount(async () => {
    if (!gameSession) return;

    try {
      await finishGame(gameSession, score);
      bestGame = getHighestScoreGameSession();
    } finally {
      loadingBestGame = false;
    }
  });

  let error = false;
  let email = $user?.email ?? '';
  let hasApprovedMarketingConsent = false;
  let isCouponRedemptionExpanded = false;

  const [copiedShareUrl, setCopiedShareUrl] = createTemporaryValue(false);
  const [copiedCoupon, setCopiedCoupon] = createTemporaryValue(false);

  const isWebShareApiAvailable = () => {
    return browser && typeof navigator.share === 'function';
  };

  const shareBestGame = async () => {
    if (!bestGame?.score) {
      return;
    }

    const searchParams = new URLSearchParams($page.url.search);
    searchParams.set(SHARED_GAME_SESSION_ID_QUERY_PARAM_NAME, bestGame.id);

    const bestGamePath = $buildPath({
      path: sharePagePath,
      searchQuery: searchParams.toString()
    });

    const bestGameUrl = new URL(bestGamePath, $page.url).toString();

    if (isWebShareApiAvailable()) {
      navigator.share({
        title: $t('game.summary.share.title', { score: bestGame.score.toString() }),
        url: bestGameUrl
      });
    } else {
      await navigator.clipboard.writeText(bestGameUrl);
      setCopiedShareUrl(true);
    }
  };

  let loading = false;
  const handleSubmit = async () => {
    error = false;
    loading = true;

    try {
      bestGame = await redeemDiscount(email, hasApprovedMarketingConsent);
    } catch (_) {
      error = true;
    }

    loading = false;
  };
</script>

<div
  transition:fade={{ duration: 200 }}
  class="absolute z-10 inset-0 bg-primary/10 pointer-events-none"
/>
<div
  transition:fade={{ duration: 200 }}
  class="absolute z-20 bottom-3 left-3 right-3 max-w-[320px] mx-auto text-center bg-primary/10 rounded-md p-3"
>
  <p class="text-sm leading-relaxed text-highlight">
    {$t('game.summary.help')}
  </p>
</div>
<div
  in:scale={{ start: 0.8, duration: 150, easing: quintOut }}
  out:fly={{ y: -20, duration: 100, easing: quintInOut }}
  class="pointer-events-auto absolute z-20 top-1/2 -translate-y-1/2 left-3 right-3 max-w-[320px] mx-auto"
>
  <div class="bg-highlight rounded-md p-4 flex text-center gap-3 shadow-game">
    <div class="flex-1">
      <span class="text-tertiary block">{$t('game.summary.score')}</span>
      <span class="text-primary text-6xl block mt-2">{score}</span>
      <Button
        name="restart"
        size="sm"
        on:click={() => {
          dispatch('restart');
        }}
        variant="primary"
        class="w-full mt-3">{$t('game.summary.restart')}</Button
      >
    </div>
    <Divider class="h-auto" orientation="vertical" />
    {#if loadingBestGame}
      <div class="flex-1 height-full flex items-center justify-center">
        <Spinner />
      </div>
    {:else}
      <div class="flex-1">
        <span class="text-accent block">{$t('game.summary.best')}</span>
        <span class="text-accent text-6xl block mt-2">{bestGame?.score ?? '-'}</span>

        {#if isWebShareApiAvailable()}
          <Button
            name="share"
            size="sm"
            on:click={() => shareBestGame()}
            variant="ghost"
            disabled={!bestGame?.score || bestGame.score === 0}
            class="w-full mt-3"
          >
            {$t('game.summary.share')}
          </Button>
        {:else}
          <Button
            name="share"
            size="sm"
            on:click={() => shareBestGame()}
            variant="ghost"
            class="w-full mt-3"
            disabled={!bestGame?.score || bestGame.score === 0}
          >
            {$copiedShareUrl ? $t('game.summary.copied') : $t('game.summary.share')}
          </Button>
        {/if}
      </div>
    {/if}
  </div>

  <div class="bg-highlight rounded-md justify-center mt-3 shadow-game">
    <button
      name="coupon"
      class="w-full flex items-center p-4 justify-between"
      class:pointer-events-none={!bestGame?.discountAmount || bestGame.discountAmount <= 0}
      on:click={() => {
        if (!bestGame?.discountAmount || bestGame.discountAmount <= 0) return;

        isCouponRedemptionExpanded = !isCouponRedemptionExpanded;
      }}
    >
      {#if bestGame?.discountAmount && bestGame.discountAmount > 0}
        <span class="flex items-center gap-2">
          <Icon class="text-accent" icon="discount" size="md" />
          <span>
            <Translation key="game.summary.coupon-value" let:match>
              {#if match === 'value'}
                <span class="text-accent">
                  {#if $currentLocation == 'au' || $currentLocation == 'ca'}
                    {baseFormatCurrency(
                      `${$lang}-${$currentLocation}`,
                      $currency,
                      bestGame.discountAmount
                    )}
                  {:else}
                    {$formatCurrency($currency, bestGame.discountAmount)}
                  {/if}
                </span>
              {/if}
            </Translation>
          </span>
        </span>
      {:else if loadingBestGame}
        <Skeleton class="h-4 w-40 rounded-md" />
      {:else}
        <span class="text-primary/40">{$t('game.summary.not-enough')}</span>
      {/if}
      {#if bestGame?.discountCode}
        <span class="text-sm">{$t('game.summary.claimed')}</span>
      {:else if bestGame?.discountAmount && bestGame.discountAmount > 0}
        <div class="flex items-center justify-center">
          <Icon size="sm" icon={isCouponRedemptionExpanded ? 'minus-bold' : 'plus-bold'} />
        </div>
      {/if}
    </button>

    {#if bestGame?.discountCode}
      <div class="p-4 pt-0 text-center flex flex-col gap-2">
        <p class="font-[monospace] text-lg p-2 border bg-primary/2 rounded-md">
          {bestGame.discountCode}
        </p>
        <Button
          name="copy-coupon"
          class="w-full"
          variant="accent"
          size="sm"
          on:click={async () => {
            if (!bestGame || !bestGame.discountCode) return;
            await navigator.clipboard.writeText(bestGame.discountCode);
            setCopiedCoupon(true);
          }}
        >
          {$copiedCoupon ? $t('game.summary.copied') : $t('game.summary.copy-coupon')}
        </Button>
        <p class="text-sm text-primary/40">
          Valid until {format(new Date(bestGame.discountExpiresAt), 'dd/MM/yyyy')}
        </p>
      </div>
    {/if}

    {#if !bestGame?.discountCode}
      <div>
        {#if isCouponRedemptionExpanded}
          <div class="p-4 pt-0" transition:slide={{ duration: 200 }}>
            <form class="mt-3" on:submit|preventDefault={handleSubmit}>
              <Input
                autofocus
                {error}
                type="email"
                bind:value={email}
                placeholder={$t('email-input.placeholder')}
                class="w-full"
              />

              {#if error}
                <p class="mt-2 text-sm text-danger">{$t('error.generic.title')}</p>
              {:else}
                <p class="mt-2 text-sm text-primary/40">{$t('game.summary.single-use')}</p>
              {/if}

              <label for="newsletter" class="mt-4 flex gap-2 items-center">
                <Checkbox id="newsletter" bind:checked={hasApprovedMarketingConsent} />
                <span>{$t('game.summary.newsletter')}</span>
              </label>
              <Button
                name="claim"
                class="mt-4 w-full"
                variant="accent"
                type="submit"
                disabled={!email}
                {loading}
              >
                {$t('game.summary.claim')}
              </Button>
            </form>
          </div>
        {/if}
      </div>
    {/if}
  </div>
</div>
